/**
 * Utility to handle API URL generation for both web and electron environments
 */

// Determine if we're running in Electron
const isElectron = 'electron' in window && (window as any).electron?.isElectron === true;

/**
 * Determine if we're running in an auto-print context (server-side Puppeteer rendering).
 * Auto-print pages are accessed with a printToken URL parameter and rendered by the
 * backend's Puppeteer instance. In this context, we must use relative URLs because
 * the Puppeteer browser navigates directly to the Azure App Service URL, bypassing
 * Cloudflare, but the Vue app's VUE_APP_API_URL points to the Cloudflare-protected domain.
 */
function isAutoPrintContext(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('printToken');
}

/**
 * Get the base API URL based on the environment
 * In Electron, we use relative URLs to leverage the proxy
 * In auto-print context, we use relative URLs to stay on the same origin (Azure direct)
 * In web, we use the configured VUE_APP_API_URL
 */
export function getApiUrl(path: string = ''): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (isElectron || isAutoPrintContext()) {
    // In Electron or auto-print context, use relative URLs
    return normalizedPath;
  }

  // In web environment, use the configured API URL
  const baseUrl = process.env.VUE_APP_API_URL || '';
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Get the reports URL based on the environment
 */
export function getReportsUrl(path: string = ''): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  if (isElectron) {
    return `/reporting${normalizedPath}`;
  }
  
  const baseUrl = process.env.VUE_APP_REPORTS_URL || '/reporting';
  return `${baseUrl}${normalizedPath}`;
}